import { memo, useEffect, useRef, useState } from "react"
import { Stack, Text, Center, Button, Container, Box, Input, HStack, IconButton, Card, Separator, Textarea } from "@chakra-ui/react"
import { useCart } from "contexts/cart"
import { formatCurrency } from "@/utilities/number"
import { useApp } from "contexts/app"
import { useForm } from "react-hook-form"
import { Field } from "@/components/ui/field"
import { useNavigate } from "react-router"
import ProfileService from "services/profile"
import BottomSheet from "components/bottom-sheet"
import { LuPencil, LuPlus } from "react-icons/lu"

const profileService = new ProfileService();

const createTempId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const MemberCheckout = () => {
    const { setProcessing, setPageTitle, showToast } = useApp();
    const { cart, updateShippingAddress, completeCart, create } = useCart();
    const formRef = useRef();
    const navigate = useNavigate();

    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [showAddressPicker, setShowAddressPicker] = useState(false);
    const [showNewForm, setShowNewForm] = useState(false);
    const [newAddr, setNewAddr] = useState({ full_name: "", phone_number: "", address: "" });
    const [savingNew, setSavingNew] = useState(false);

    useEffect(() => {
        setPageTitle("Thanh toán")
    }, [setPageTitle])

    const { register, handleSubmit, formState: { errors }, setValue } = useForm();

    const fillForm = (addr) => {
        setValue("phone_number", addr?.phone_number || addr?.phone || "");
        setValue("full_name", addr?.full_name || "");
        setValue("address", addr?.address || "");
    };

    useEffect(() => {
        const load = async () => {
            try {
                const data = await profileService.get();
                const list = Array.isArray(data?.info?.shipping_address) ? data.info.shipping_address : [];
                setSavedAddresses(list);
                const defaultAddr = list.find(x => x.is_default) || list[0];
                const addr = {
                    phone_number: defaultAddr?.phone_number || data?.info?.phone || "",
                    full_name: defaultAddr?.full_name || data?.info?.full_name || "",
                    address: defaultAddr?.address || data?.info?.address || "",
                };
                setSelectedAddress(addr);
                fillForm(addr);
                // nếu chưa có địa chỉ nào thì mở sẵn form tạo mới
                if (list.length === 0) {
                    setNewAddr({ full_name: data?.info?.full_name || "", phone_number: data?.info?.phone || "", address: "" });
                    setShowNewForm(true);
                }
            } catch (_) { }
        };
        load();
    }, []);

    const pickAddress = (addr) => {
        const sel = { phone_number: addr.phone_number, full_name: addr.full_name, address: addr.address };
        setSelectedAddress(sel);
        fillForm(sel);
        setShowAddressPicker(false);
        setShowNewForm(false);
    };

    const saveNewAddress = async () => {
        if (!newAddr.address?.trim()) {
            showToast({ description: "Vui lòng nhập địa chỉ giao hàng", type: "error" });
            return;
        }
        setSavingNew(true);
        try {
            const currentData = await profileService.get();
            const currentList = Array.isArray(currentData?.info?.shipping_address) ? currentData.info.shipping_address : [];
            const newItem = {
                id: createTempId(),
                full_name: newAddr.full_name?.trim() || currentData?.info?.full_name || "",
                phone_number: newAddr.phone_number?.trim() || currentData?.info?.phone || "",
                address: newAddr.address.trim(),
                is_default: currentList.length === 0,
            };

            const profileFullName = String(
                currentData?.info?.full_name || newItem.full_name || selectedAddress?.full_name || ""
            ).trim();
            const profileAddress = String(
                currentData?.info?.address || newItem.address || selectedAddress?.address || ""
            ).trim();

            if (!profileFullName) {
                showToast({ description: "Vui lòng nhập họ tên người nhận", type: "error" });
                return;
            }

            if (!profileAddress) {
                showToast({ description: "Vui lòng nhập địa chỉ giao hàng", type: "error" });
                return;
            }

            const updatedList = [...currentList, newItem];
            await profileService.updateInfo({
                full_name: profileFullName,
                address: profileAddress,
                shipping_address_list: updatedList,
            });
            setSavedAddresses(updatedList);
            pickAddress(newItem);
            setNewAddr({ full_name: "", phone_number: "", address: "" });
            setShowNewForm(false);
        } catch (e) {
            showToast({ description: e?.message || "Lưu địa chỉ thất bại", type: "error" });
        } finally {
            setSavingNew(false);
        }
    };

    const onCartComplete = async (data) => {
        setProcessing(true);
        await updateShippingAddress(data);
        try {
            var order = await completeCart();
            await create();
            setProcessing(false);
            showToast({ description: "Vui lòng thanh toán để hoàn tất đơn hàng", type: "success" });
            navigate(`/order-pay/${order.id}`);
        } catch (error) {
            if (error.error_code === "voucher_not_allow") {
                showToast({
                    description: "Không thể áp dụng mã khuyến mãi. Vui lòng xoá mã khuyến mãi để tiếp tục",
                    type: "error",
                    duration: 6000,
                });
                navigate("/cart");
            }
            setProcessing(false);
        }
    };

    const onSubmit = handleSubmit(onCartComplete);

    return (
        <div style={{ paddingBottom: 75 }}>
            <form ref={formRef} onSubmit={onSubmit}>
                <Stack gap="4" align="flex-start" maxW="sm">
                    <Text>Thông tin giao hàng</Text>

                    <Card.Root size="sm" variant="subtle" width="100%">
                        <Card.Body>
                            <HStack gap={3} align="start">
                                <Stack gap={1} flex={1}>
                                    <HStack gap={2} wrap="wrap">
                                        <Text fontWeight="semibold">{selectedAddress?.full_name || "-"}</Text>
                                        <Text color="fg.muted" fontSize="sm">{selectedAddress?.phone_number || ""}</Text>
                                    </HStack>
                                    <Text fontSize="sm">{selectedAddress?.address || "Chưa có địa chỉ giao hàng"}</Text>
                                </Stack>
                                <IconButton
                                    variant="ghost" size="sm"
                                    title="Đổi địa chỉ"
                                    onClick={() => setShowAddressPicker(true)}
                                >
                                    <LuPencil />
                                </IconButton>
                            </HStack>
                        </Card.Body>
                    </Card.Root>

                    {/* Hidden inputs để react-hook-form ghi nhận khi submit */}
                    <div style={{ display: "none" }}>
                        <input {...register("phone_number")} />
                        <input {...register("full_name")} />
                        <input {...register("address")} />
                    </div>

                    <Field label="Ghi chú">
                        <Input {...register("note")} placeholder="Ghi chú thêm cho đơn hàng" />
                    </Field>

                </Stack>
            </form>

            <BottomSheet
                open={showAddressPicker || showNewForm}
                onOpenChange={(open) => { if (!open) { setShowAddressPicker(false); setShowNewForm(false); } }}
                title={showNewForm && savedAddresses.length === 0 ? "Thêm địa chỉ giao hàng" : "Chọn địa chỉ giao hàng"}
                bodyPaddingX={3}
                bodyPaddingBottom={3}
            >
                <Stack gap={3} pb={4}>
                    {!showNewForm && savedAddresses.map((addr, index) => (
                        <Card.Root
                            key={addr.id || index}
                            size="sm"
                            variant="outline"
                            cursor="pointer"
                            onClick={() => pickAddress(addr)}
                        >
                            <Card.Body>
                                <HStack gap={3} align="start">
                                    <Stack gap={1} flex={1}>
                                        <HStack gap={2}>
                                            <Text fontWeight="semibold">{addr.full_name}</Text>
                                            {addr.is_default && (
                                                <Text color="green.600" fontSize="xs" fontWeight="medium">Mặc định</Text>
                                            )}
                                        </HStack>
                                        <Text fontSize="sm" color="fg.muted">{addr.phone_number}</Text>
                                        <Text fontSize="sm">{addr.address}</Text>
                                    </Stack>
                                </HStack>
                            </Card.Body>
                        </Card.Root>
                    ))}

                    {!showNewForm && (
                        <Button variant="outline" onClick={() => setShowNewForm(true)}>
                            <LuPlus />
                            Thêm địa chỉ mới
                        </Button>
                    )}

                    {showNewForm && (
                        <Stack gap={3}>
                            {savedAddresses.length > 0 && (
                                <Button variant="ghost" size="sm" onClick={() => setShowNewForm(false)}>← Danh sách địa chỉ</Button>
                            )}
                            <Stack gap={1}>
                                <Text color="fg.muted" fontSize="sm">Người nhận</Text>
                                <Input
                                    value={newAddr.full_name}
                                    onChange={(e) => setNewAddr(p => ({ ...p, full_name: e.target.value }))}
                                />
                            </Stack>
                            <Stack gap={1}>
                                <Text color="fg.muted" fontSize="sm">Số điện thoại nhận hàng</Text>
                                <Input
                                    value={newAddr.phone_number}
                                    onChange={(e) => setNewAddr(p => ({ ...p, phone_number: e.target.value }))}
                                />
                            </Stack>
                            <Stack gap={1}>
                                <Text color="fg.muted" fontSize="sm">Địa chỉ giao hàng</Text>
                                <Textarea
                                    value={newAddr.address}
                                    onChange={(e) => setNewAddr(p => ({ ...p, address: e.target.value }))}
                                    rows={3}
                                />
                            </Stack>
                            <Button onClick={saveNewAddress} loading={savingNew}>Lưu địa chỉ này</Button>
                        </Stack>
                    )}
                </Stack>
            </BottomSheet>

            <div style={{ position: "fixed", bottom: 0, left: 0, padding: 0, width: "100vw" }}>
                <Container maxW="md" style={{ padding: 0 }}>
                    <Box bg="bg" shadow="md">
                        <div style={{ display: "flex", padding: 16 }}>
                            <div style={{ flexGrow: 1 }}>
                                <Center height="100%">
                                    <Text fontWeight="semibold">{formatCurrency(cart?.total_amount)}</Text>
                                </Center>
                            </div>
                            <div>
                                <Button onClick={() => handleSubmit(onSubmit)()}>Hoàn tất</Button>
                            </div>
                        </div>
                    </Box>
                </Container>
            </div>
        </div>
    );
};

export default memo(MemberCheckout);
